import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * The field paper band on the homepage.
 *
 * Deliberately not wired to /api/papers. The homepage is the one page whose
 * first paint we actually defend, and a fetch here would buy a loading state
 * and a layout shift to display two lines of marketing copy that change once
 * or twice a year. When paper 02 ships, this file changes with it.
 */
const FEATURED = {
  slug: 'the-harness-layer',
  number: '01',
  title: 'The Harness Layer',
  meta: 'September 2026 · 29 pages · for CIOs and CTOs',
  dek:
    'Every serious coding agent has converged on the same architecture. Almost nobody has '
    + 'written down what that means for the business running it.',
  cover: '/paper-art/harness-layer-cover.jpg',
};

export default function PapersTeaser() {
  return (
    <div className="mt-20 md:mt-24 pt-16 border-t border-[var(--border)]">
      <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
        <span className="eyebrow">Field papers</span>
        <span className="flex-1 thin-line ml-2" />
        <Link
          to="/papers"
          className="mono text-[0.68rem] tracking-[0.14em] uppercase hover:text-[var(--accent)] transition-colors whitespace-nowrap"
        >
          All papers
        </Link>
      </div>

      <div className="grid md:grid-cols-[minmax(0,260px)_1fr] gap-10 md:gap-16 items-center">
        <Link to={`/papers/${FEATURED.slug}`} className="group relative block" aria-label={FEATURED.title}>
          <div
            aria-hidden="true"
            className="absolute -inset-6 rounded-full blur-3xl opacity-[0.18]
                       bg-[radial-gradient(ellipse_at_center,var(--accent),transparent_65%)]"
          />
          <img
            src={FEATURED.cover}
            alt={`${FEATURED.title} — cover`}
            width="990"
            height="1400"
            loading="lazy"
            className="relative w-full max-w-[260px] rounded-sm border border-[var(--border-2)]
                       shadow-[0_34px_70px_-20px_rgba(0,0,0,0.9)]
                       transition-transform duration-500 ease-out group-hover:-translate-y-1.5"
          />
        </Link>

        <div className="max-w-[54ch]">
          <div className="mono text-[0.68rem] tracking-[0.18em] uppercase text-[var(--accent)] mb-4">
            №&nbsp;{FEATURED.number} · {FEATURED.meta}
          </div>
          <h3 className="serif text-[2.2rem] md:text-[3rem] leading-[1.06] tracking-[-0.025em]">
            {FEATURED.title}
          </h3>
          <p className="text-[var(--text-2)] text-[1rem] leading-[1.7] mt-5">{FEATURED.dek}</p>
          <p className="text-[var(--text-3)] text-[0.92rem] leading-[1.65] mt-4">
            Three published results that do not agree with each other, the one comparison that
            holds the model fixed, and what it costs to build the layer yourself. Free to read.
          </p>
          <Link to={`/papers/${FEATURED.slug}`} className="btn-primary mt-8">
            Read the paper <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}
